import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MutationOptions, MutationOptionsWithResponse } from '../../../services/general.types';
import { useIntegrationService } from '../../../services/integration.service';
import { saveUserIntegrationsStandalone } from '../../../services/user-integrations.service';
import { QUERY_KEYS } from '../../../utils/queryKeys';

interface CreateExchangeIntegrationParams {
    importerUri: string;
    data: any;
}

interface DeleteIntegrationParams {
    name: string;
    jobId: number;
    jobTableName: string;
}

interface ImportCsvParams {
    importerUri: string;
    file: File;
}

export const useIntegrationMutations = () => {
    const queryClient = useQueryClient();
    const integrationService = useIntegrationService();

    const invalidateQueries = () => {
        QUERY_KEYS.dataQueriesWithTransactions().forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
        });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REFRESH_BALANCE] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
    };

    const updateIntegrationSetupState = async () => {
        try {
            // Set integration setup state to 'completed' since user has added their first integration
            await saveUserIntegrationsStandalone([], 'completed');
            // Invalidate the integration setup state query to update the UI
            queryClient.invalidateQueries({ queryKey: ['integrationSetupState'] });
        } catch (error) {
            console.error('Error updating integration setup state:', error);
        }
    };

    // Create Exchange Integration
    const { mutate: baseCreateExchangeIntegration, isPending: isExchangeCreating } = useMutation({
        mutationKey: ['createIntegration'],
        mutationFn: (params: CreateExchangeIntegrationParams) =>
            integrationService.createExchangeIntegration(params.importerUri, params.data),
        onSuccess: () => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const createExchangeIntegration = (data: CreateExchangeIntegrationParams, options?: MutationOptions) => {
        baseCreateExchangeIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Create Wallet Integration
    const { mutate: baseCreateWalletIntegration, isPending: isWalletCreating } = useMutation({
        mutationKey: ['createIntegration'],
        mutationFn: (params: any) => integrationService.createWalletIntegration(params),
        onSuccess: () => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const createWalletIntegration = (data: any, options?: MutationOptions) => {
        baseCreateWalletIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Delete Integration
    const { mutate: baseDeleteIntegration, isPending: isDeleting } = useMutation({
        mutationKey: ['deleteIntegration'],
        mutationFn: (params: DeleteIntegrationParams) =>
            integrationService.deleteIntegration(params.name, params.jobId, params.jobTableName),
        onSuccess: () => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            console.error('Failed to delete integration:', error);
        },
    });

    const deleteIntegration = (data: DeleteIntegrationParams, options?: MutationOptions) => {
        baseDeleteIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Import CSV
    const { mutate: baseImportCsv, isPending: isCsvImporting } = useMutation({
        mutationKey: ['integrationImportCsv'],
        mutationFn: (params: ImportCsvParams) => {
            return integrationService.importCsv(params.importerUri, params.file, [], () => {});
        },
        onSuccess: (response) => {
            invalidateQueries();
            updateIntegrationSetupState();
        },
        onError: (error) => {
            invalidateQueries();
        },
    });

    const importCsv = (data: ImportCsvParams, options?: MutationOptionsWithResponse) => {
        baseImportCsv(data, {
            onSuccess: (response) => {
                options?.onSuccess?.(response); // Pass the response to onSuccess callback
            },
            onError: options?.onError,
        });
    };

    return {
        createExchangeIntegration,
        createWalletIntegration,
        deleteIntegration,
        importCsv,
        isExchangeCreating,
        isWalletCreating,
        isCsvImporting,
        isDeleting,
    };
};
